import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Counter from "@/components/Counter";

function HeroSolutions() {
  const { t } = useTranslation();
  const tiles = [
    { icon: "air", key: "air", color: "bg-brand", delay: 0 },
    { icon: "water_drop", key: "water", color: "bg-secondary", delay: 0.2 },
    { icon: "factory", key: "stack", color: "bg-brand-dark", delay: 0.4 },
    { icon: "volume_up", key: "noise", color: "bg-brand", delay: 0.1 },
    { icon: "directions_boat", key: "maritime", color: "bg-secondary", delay: 0.3 },
  ];

  return (
    <section className="relative pt-32 pb-xl overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(144,184,73,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(81,95,116,0.18),transparent_55%)]" />
        <div className="absolute inset-0 tech-grid opacity-40" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-md items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-md inline-block">
              {t("solutions.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-h1 text-primary mb-md">
              {t("solutions.hero.title")} <span className="text-gradient">{t("solutions.hero.title_accent")}</span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              {t("solutions.hero.subtitle")}
            </p>

            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="text-h3 font-bold text-brand"><Counter end={5} /></div>
                <div className="text-label-caps text-zinc-500 mt-1">{t("solutions.hero.stat_countries")}</div>
              </div>
              <div>
                <div className="text-h3 font-bold text-brand"><Counter end={2000} />+</div>
                <div className="text-label-caps text-zinc-500 mt-1">{t("solutions.hero.stat_devices")}</div>
              </div>
              <div>
                <div className="text-h3 font-bold text-brand"><Counter end={1200} />+</div>
                <div className="text-label-caps text-zinc-500 mt-1">{t("solutions.hero.stat_companies")}</div>
              </div>
            </div>
          </motion.div>

          <div className="relative h-[440px]">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full glass-card flex items-center justify-center shadow-xl"
            >
              <div className="text-center">
                <span className="material-symbols-outlined icon-fill text-brand text-5xl">hub</span>
                <div className="font-manrope font-bold text-primary text-sm mt-1">{t("solutions.hero.core_label")}</div>
              </div>
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-brand/30 orbit" />
              <div className="absolute -inset-3 rounded-full border border-dashed border-secondary/30 orbit-reverse" />
            </motion.div>

            {tiles.map((tl, i) => {
              const angle = (i / tiles.length) * Math.PI * 2 - Math.PI / 2;
              const r = 170;
              const x = Math.cos(angle) * r;
              const y = Math.sin(angle) * r;
              return (
                <motion.div
                  key={tl.key}
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
                  style={{ left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                >
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: tl.delay }}
                    className="glass-card px-4 py-3 rounded-xl flex items-center gap-2 shadow-md min-w-[120px]"
                  >
                    <span className={`w-8 h-8 rounded-lg ${tl.color} flex items-center justify-center`}>
                      <span className="material-symbols-outlined text-white text-[18px]">{tl.icon}</span>
                    </span>
                    <span className="font-manrope font-bold text-sm text-primary">{t(`solutions.hero.tiles.${tl.key}`)}</span>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const moduleDefs = [
  { key: "air", span: "md:col-span-8", border: "border-l-brand", icon: "air", iconColor: "text-brand", tags: ["PM2.5 / PM10", "SO2 / NO2", "O3 / CO"], img: "/imagery/kocems-mock-2.png" },
  { key: "noise", span: "md:col-span-4", border: "border-l-secondary", icon: "volume_up", iconColor: "text-secondary", tags: ["Leq (dB)", "Lmax / Lmin"] },
  { key: "water", span: "md:col-span-4", border: "border-l-secondary", icon: "water_drop", iconColor: "text-secondary", tags: ["COD / BOD", "TSS / pH"] },
  { key: "stack", span: "md:col-span-8", border: "border-l-brand", icon: "factory", iconColor: "text-brand", tags: ["Flue Gas Flow", "Mercury (Hg)", "HCL / HF"], img: "https://lh3.googleusercontent.com/aida-public/AB6AXuAPLhLUl-JYZUi_T7JeryaA3SR6lvX8Af0uRqnmtbJfUtiMHOTh1hZVVNLLIFot0qJVv4uGtq-mQN0BG--yXIY8Y-FWn_R9-skSdz7GsHzQgV2-uXiSBmZQ5AdXT3bNPrZ2PepR3kPQwa3-kbw6GUljEItKQ4tx4AEnbypDruLLZ3yfbnGOoX2pITQU81vvHoFh0Z8oLKQJ8e0Mu2E54osXK5Mn6xqNevOs1V3uLPVl_SVpjcn16lmeMdYtbrkY-dprapfvdN41tg" },
  { key: "maritime", span: "md:col-span-12", border: "border-l-brand", icon: "directions_boat", iconColor: "text-brand", tags: ["Port Air Quality", "Sea Salinity", "Vessel Exhaust", "Meteorology"] },
];

function Modules() {
  const { t } = useTranslation();
  return (
    <section className="max-w-[1280px] mx-auto px-6 mb-xl">
      <div className="mb-lg">
        <h2 className="font-h2 text-h2 text-primary">{t("solutions.modules_title")}</h2>
        <div className="w-20 h-1 bg-brand mt-sm" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter">
        {moduleDefs.map((m, idx) => {
          const title = t(`solutions.modules.${m.key}.title`);
          const desc = t(`solutions.modules.${m.key}.desc`);
          return (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: idx * 0.08 }}
              className={`${m.span} glass-panel p-md rounded-xl flex flex-col ${m.img ? "md:flex-row" : ""} gap-md border-l-4 ${m.border} hover:shadow-lg transition-shadow`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-sm mb-sm">
                  <span className={`material-symbols-outlined ${m.iconColor}`}>{m.icon}</span>
                  <h3 className="font-h3 text-h3 text-primary">{title}</h3>
                </div>
                <p className="font-body-md text-on-surface-variant mb-md">{desc}</p>
                <div className="flex flex-wrap gap-xs">
                  {m.tags.map((tag) => (
                    <span key={tag} className="bg-surface-container-high px-3 py-1 rounded-full text-xs font-semibold">{tag}</span>
                  ))}
                </div>
              </div>
              {m.img && (
                <div className="w-full md:w-64 h-48 rounded-lg overflow-hidden shrink-0">
                  <img alt={title} className="w-full h-full object-cover" src={m.img} />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Ecosystem() {
  const { t } = useTranslation();
  const layers = t("solutions.ecosystem.layers", { returnObjects: true }) || [];
  const layerIcons = ["sensors", "hub", "dashboard_customize"];
  return (
    <section className="bg-primary-container py-xl text-surface relative overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-50" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="text-center mb-lg">
          <span className="font-label-caps text-on-tertiary-fixed text-xs tracking-widest block mb-sm">{t("solutions.ecosystem.eyebrow")}</span>
          <h2 className="font-h2 text-h2 mb-md">{t("solutions.ecosystem.title")}</h2>
          <p className="text-on-primary-container max-w-2xl mx-auto font-body-md">
            {t("solutions.ecosystem.intro")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
          {layers.map((l, i) => (
            <motion.div
              key={l.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="text-center group"
            >
              <div className="w-20 h-20 bg-tertiary-container rounded-full flex items-center justify-center mx-auto mb-md border border-brand/30 group-hover:bg-brand transition-colors">
                <span className="material-symbols-outlined text-brand group-hover:text-white text-3xl">{layerIcons[i]}</span>
              </div>
              <h4 className="font-h3 text-xl mb-xs text-white">{l.title}</h4>
              <p className="text-sm text-on-primary-container">{l.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Solutions() {
  return (
    <>
      <HeroSolutions />
      <Modules />
      <Ecosystem />
    </>
  );
}
